// @ts-ignore
import { data as css } from './unit-editor-component.css';

import '../../components/command-button';
import '../../components/command-toggle';
import './components/asset-bin';
import './components/asset-item';
import './components/draggable-handle';
import AssetBin from './components/asset-bin';
import Signal from '../../control-flow/signal';
import elt from '../../html/elt';
import injectCSS from '../../html/inject-css';
import logging from '../../logging';
import { HandleDragEvent } from './components/draggable-handle';
import { UnitEditorAssetBin } from "../../domain";
import { html } from '../../html/html';
import { registerListener } from '../../html/register-listener';

const log = logging('components');

type DoorAppearance = "solid" | "transparent" | "invisible";

export default class UnitEditorComponent extends HTMLElement {

    // The viewport in CSS pixels at the time the resize handle is grabbed
    initialViewportWidth: number;

    viewportResize: HTMLElement;
    viewportContainer: HTMLElement;
    propertiesContainer: HTMLElement;
    accordionContainer: HTMLElement;

    btnUndo: HTMLElement;
    btnRedo: HTMLElement;
    btnDeleteSelection: HTMLElement;
    btnSubmit: HTMLButtonElement;
    btnSaveUnit: HTMLButtonElement;
    btnSaveAsIslandUnit: HTMLButtonElement;
    btnPartAttributes: HTMLButtonElement;
    btnRecalc: HTMLButtonElement;

    assetBins: UnitEditorAssetBin[];

    onSubmit = new Signal('onSubmit', 'unit-editor');
    onSaveUnit = new Signal('onSaveUnit', 'unit-editor');
    onDoorAppearance = new Signal<[DoorAppearance]>(
        'onDoorAppearance', 'unit-editor');

    constructor(assetBins: UnitEditorAssetBin[]) {
        super();
        log.debug('UnitEditorComponent()');

        injectCSS(css);

        this.assetBins = assetBins;
        this.accordionContainer = elt('div', {class: 'accordion'});
        this.viewportResize = elt('draggable-handle');
        this.viewportContainer = elt('div', {class: 'viewport-container'}, this.viewportResize);
        this.propertiesContainer = elt('div', {class: 'properties-container'});

        this.btnSubmit = elt('button', {class: 'btn btn-primary'}, 'Submit');
        this.btnSubmit.addEventListener('click', this.onSubmit.trigger);
        this.btnSaveUnit =
            elt('button', {class: 'btn btn-primary'}, 'Save Unit');
        this.btnSaveUnit.addEventListener('click', this.onSaveUnit.trigger);
        this.btnSaveAsIslandUnit =
            elt('button', {class: 'btn btn-primary'}, 'Save as Island Unit');
        this.btnPartAttributes =
            elt('button', {class: 'btn btn-primary'}, 'Part Attributes');
        this.btnRecalc =
            elt('button', {class: 'btn btn-primary'}, 'Recalc (Admin Only)');

        registerListener(this.viewportResize, 'handle-grab', this.startResizeViewport);
        registerListener(this.viewportResize, 'handle-drag', this.resizeViewport);
    }

    connectedCallback() {
        this.append(html(TOOLBAR_HTML));
        this.append(this.viewportContainer);
        this.append(this.propertiesContainer);
        const assetsContainer = elt('div', {class: 'assets'},
            elt('div', {class: 'scroll-container'}, this.accordionContainer));
        this.append(assetsContainer);
        this.append(
            elt('div', {class: 'save-buttons'},
                elt('command-button',
                    {
                        channel: 'unit-editor',
                        action: 'launch-viewer',
                        title: 'Launch Viwer',
                        class: 'btn btn-primary',
                    },
                    elt('i', {class: 'bi bi-eye'})),
                elt('command-button',
                    {
                        channel: 'unit-editor',
                        action: 'upload-json',
                        title: 'Upload JSON',
                        class: 'btn btn-primary',
                    },
                    elt('i', {class: 'bi bi-folder2-open'})),
                elt('command-button',
                    {
                        channel: 'unit-editor',
                        action: 'download-json',
                        title: 'Download JSON',
                        class: 'btn btn-primary',
                    },
                    elt('i', {class: 'bi bi-download'})),
                this.btnSubmit,
                this.btnSaveUnit,
                this.btnSaveAsIslandUnit,
                this.btnPartAttributes,
                this.btnRecalc,
               ));

        this.btnUndo =
            this.querySelector('command-button[action=undo]') as HTMLElement;
        this.btnRedo =
            this.querySelector('command-button[action=redo]') as HTMLElement;
        this.btnDeleteSelection =
            this.querySelector('command-button[action=delete-selection]') as HTMLElement;

        for(const btn of this.querySelectorAll('.show-doors-select')) {
            registerListener(btn, 'click', (event: MouseEvent) => {
                const how =
                    (event.currentTarget as HTMLElement).innerText.toLowerCase() as DoorAppearance;
                this.setDoorAppearance(how);
                this.onDoorAppearance.trigger(how);
            });
        }

        const btnFront = this.querySelector('command-button[action=view-front]');
        const btn3D = this.querySelector('command-button[action=view-3d]');
        btnFront.addEventListener('click', () => {
            btn3D.classList.remove('active');
            btnFront.classList.add('active');
        });
        btn3D.addEventListener('click', () => {
            btnFront.classList.remove('active');
            btn3D.classList.add('active');
        });
    }

    addBin(bin: UnitEditorAssetBin): AssetBin {
        const binElt: AssetBin = elt('asset-bin', {label: bin.label});
        this.accordionContainer.append(binElt);
        return binElt;
    }

    setDoorAppearance(how: DoorAppearance) {
        for(const btn of this.querySelectorAll('.show-doors-select')) {
            if((btn as HTMLElement).innerText.toLowerCase() == how) {
                btn.classList.add('active');
            } else {
                btn.classList.remove('active');
            }
        }
    }

    startResizeViewport = () => {
        this.initialViewportWidth = this.viewportContainer.clientWidth;
    }

    resizeViewport = (event: CustomEvent<HandleDragEvent>) => {
        const { dx } = event.detail;
        this.style.setProperty('--viewport-width', `${this.initialViewportWidth + dx}px`);
        // TODO: persist this property to LocalStorage
    }
}
window.customElements.define('unit-editor-component', UnitEditorComponent);


const TOOLBAR_HTML = `
<div class="toolbar">
    <command-button
        channel="unit-editor"
        action="undo"
        class="btn btn-secondary">
        Undo
    </command-button>
    <command-button
        channel="unit-editor"
        action="redo"
        class="btn btn-secondary">
        Redo
    </command-button>
    <div class="btn-group" role="group" aria-label="Switch to front or 3D views">
        <command-button
            channel="unit-editor"
            action="view-front"
            class="btn btn-secondary active"
            title="Use the front view for editing the custom unit">
            Front
        </command-button>
        <command-button
            channel="unit-editor"
            action="view-3d"
            class="btn btn-secondary"
            title="Use the 3D view to get a closer look at your design">
            3D
        </command-button>
    </div>
    <div>
        <label style="margin-right: 1rem;">Doors:</label>
        <div class="btn-group">
            <button class="show-doors-select btn btn-secondary active">Solid</button>
            <button class="show-doors-select btn btn-secondary">Transparent</button>
            <button class="show-doors-select btn btn-secondary">Invisible</button>
        </div>
    </div>
<!--
    <command-toggle
        channel="unit-editor"
        action="show-heights">
        Show Heights
    </command-toggle>
-->
    <command-button
        channel="unit-editor"
        action="delete-selection"
        class="btn btn-warning">
        <i class="bi bi-trash"></i> Delete
    </command-button>
</div>
`;
